import {
  CardRow,
  CardRows,
} from '@/components/agent-chat/card-parts.component';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type {
  BuildBudget,
  SampleEstimate,
  TryChange,
  TryFailure,
  CustomGraph,
} from '@/types/custom-playbooks';
import { stepHeadline } from '@/containers/playbooks/custom-builder/connectors';
import { nodeLabel } from '@/containers/playbooks/custom-builder/node-metadata';
import { graphTrigger } from '@/containers/playbooks/custom-builder/test-record.util';
import { tryRepairStatus } from './try-quote.util';

function creditShortfall(required: number, remaining: number): number {
  const amounts = [required, remaining].map((value) => {
    const [digits, exponent = '0'] = String(value).split('e');
    const [whole, fraction = ''] = digits.split('.');
    return {
      units: BigInt(whole + fraction),
      places: fraction.length - Number(exponent),
    };
  });
  // Subtract the quoted decimals as integers so a small shortfall stays exact.
  const places = Math.max(0, ...amounts.map((amount) => amount.places));
  const [cost, budget] = amounts.map(
    (amount) => amount.units * 10n ** BigInt(places - amount.places)
  );
  return cost > budget ? Number(`${cost - budget}e${-places}`) : 0;
}

interface TryRepairProps {
  build?: BuildBudget;
  failure?: TryFailure | null;
  graph?: CustomGraph;
  code?: string;
}

/** The failure a stopped build names; none once the build has recovered. */
function stoppedCause({ build, failure, code }: TryRepairProps) {
  const stopped =
    build?.needsUser ||
    build?.changeRequired ||
    code === 'try_change_required' ||
    code === 'build_needs_user';
  return stopped ? (failure ?? build?.lastFailure) : undefined;
}

export function TryRepair(props: TryRepairProps) {
  const { build, graph } = props;
  const status = tryRepairStatus(build);
  return (
    <div className='flex flex-col gap-2 text-sm'>
      {status && (
        <Alert>
          <AlertTitle>{status}</AlertTitle>
        </Alert>
      )}
      {stoppedCause(props)?.operations.map((operation, index) => (
        <p key={index}>
          {failureStepLabel(operation.step, graph)} ·{' '}
          {failureLabel(operation.step, operation.code)}
        </p>
      ))}
      {build?.changes?.map((change, index) => (
        <p key={index}>
          {change.label}: {change.change}
        </p>
      ))}
    </div>
  );
}

const CHANGE_STATE: Record<TryChange['change'], string> = {
  changed: 'Changed',
  removed: 'Removed',
  rewired: 'Rewired',
};

/** TryRepair on a Clu card: the stopped build's failed steps and the server's
 *  change summary as rows. The card's gray line carries tryRepairStatus. */
export function TryRepairRows(props: TryRepairProps) {
  const { build, graph } = props;
  const rows = [
    ...(stoppedCause(props)?.operations.map((operation, index) => (
      <CardRow
        key={`failure-${index}`}
        label={failureStepLabel(operation.step, graph)}
        wrap
        end={
          <span className='text-destructive'>
            {failureLabel(operation.step, operation.code)}
          </span>
        }
      />
    )) ?? []),
    ...(build?.changes?.map((change, index) => (
      <CardRow
        key={`change-${index}`}
        label={change.label}
        wrap
        end={CHANGE_STATE[change.change] ?? change.change}
      />
    )) ?? []),
  ];
  return rows.length > 0 ? (
    <CardRows label='Try changes'>{rows}</CardRows>
  ) : null;
}

function failureStepLabel(step: string, graph?: CustomGraph) {
  const nodes = graph?.nodes.filter((node) => node.type === step) ?? [];
  // The failure names a step type, not a node: only a single match is named.
  return nodes.length === 1
    ? stepHeadline(nodes[0], graphTrigger(graph!))
    : step === 'run'
      ? 'Try'
      : nodeLabel(step);
}

function failureLabel(step: string, code: string) {
  const known: Record<string, string> = {
    crm_disconnected: 'Connection unavailable',
    try_inputs_short: 'Fewer than five eligible starting records',
    insufficient_credits: 'Insufficient credits',
  };
  return (
    known[code] ??
    {
      crm_action: 'Sync failed',
      enrich: 'Enrichment failed',
      find_people: 'Search failed',
      run: 'Try failed',
    }[step] ??
    'Step failed'
  );
}

export function TryQuote({
  quote,
  graph,
}: {
  quote: SampleEstimate;
  graph?: CustomGraph;
}) {
  const { build, fullRun } = quote;
  const shortfall = creditShortfall(
    quote.maxCreditsByRecords[0] ?? 0,
    build.remainingCredits
  );
  const credits = (value: number) =>
    `${value.toLocaleString(undefined, { maximumSignificantDigits: 21 })} credits`;
  const rows = [
    [quote.brief ? 'Briefs' : 'Starting records', String(quote.records)],
    ['Maximum Try cost', `Up to ${credits(quote.maxCredits)}`],
    ...quote.byStep.map((step) => [step.label, credits(step.maxCredits)]),
    ['Available organization credits', credits(quote.balance)],
    ['Try budget', credits(build.limitCredits)],
    ['Build spent', credits(build.spentCredits)],
    ...(fullRun
      ? [
          [
            'Full-run volume',
            quote.brief
              ? '1 brief per run'
              : fullRun.recordsPerRun === null
                ? 'Unbounded'
                : `${fullRun.recordsPerRun.toLocaleString()} records per run`,
          ],
          [
            'Full-run estimate',
            fullRun.perRunMaxCredits === null
              ? 'Unavailable'
              : `Up to ${credits(fullRun.perRunMaxCredits)} per run`,
          ],
          [
            'Period estimate',
            fullRun.perPeriodMaxCredits === null
              ? 'Unavailable'
              : `Up to ${credits(fullRun.perPeriodMaxCredits)} per ${fullRun.period}`,
          ],
          ...(fullRun.budgetLimited ? [['Agent budget', 'Limited']] : []),
        ]
      : []),
  ];
  return (
    <div className='flex flex-col gap-3'>
      <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
        {rows.map(([label, value], index) => (
          <div key={index} className='contents'>
            <dt className='text-muted-foreground'>{label}</dt>
            <dd className='text-right tabular-nums'>{value}</dd>
          </div>
        ))}
      </dl>
      {shortfall > 0 && (
        <Alert variant='destructive'>
          <AlertTitle>
            Over the Try budget · {credits(shortfall)} short
          </AlertTitle>
          <Button asChild variant='link' size='sm'>
            <a href='/settings/team'>Try it budget per Try</a>
          </Button>
        </Alert>
      )}
      {quote.records === 0 && (
        <Alert variant='destructive'>
          <AlertTitle>
            No {quote.brief ? 'brief' : 'records'} to try yet: a Try needs at
            least one, and one with none can't be approved
          </AlertTitle>
        </Alert>
      )}
      {quote.balance < quote.maxCredits && (
        <Alert variant='destructive'>
          <AlertTitle>Insufficient organization credits</AlertTitle>
        </Alert>
      )}
      <TryRepair build={build} graph={graph} />
    </div>
  );
}
